/**
 * EventView view component.
 * @module components/theme/View/EventView
 */

import React from "react";
import PropTypes from "prop-types";
import { hasBlocksData } from "@plone/volto/helpers/Blocks/Blocks";
import {
  flattenHTMLToAppURL,
  flattenToAppURL,
} from "@plone/volto/helpers/Url/Url";
import { Container as SemanticContainer } from "semantic-ui-react";
import RenderBlocks from "@plone/volto/components/theme/View/RenderBlocks";
import Icon from "@plone/volto/components/theme/Icon/Icon";
import calendarSVG from "@plone/volto/icons/calendar.svg";
import config from "@plone/volto/registry";
import { injectIntl, defineMessages } from "react-intl";

const messages = defineMessages({
  addToCalendar: {
    id: "ICS-Download",
    defaultMessage: "Add to calendar",
  },
});

const isAllDayEvent = (content) => {
  if (content.whole_day === true) return true;
  if (content.start) {
    const startDate = new Date(Date.parse(content.start));
    if (startDate.getHours() === 0 && startDate.getMinutes() === 0) {
      if (content.end) {
        const endDate = new Date(Date.parse(content.end));
        const endH = endDate.getHours();
        const endM = endDate.getMinutes();
        if ((endH === 23 && endM === 59) || (endH === 0 && endM === 0))
          return true;
      } else {
        return true;
      }
    }
  }
  return false;
};

const EventTextfieldView = ({ content }) => {
  const Image = config.getComponent({ name: "Image" }).component;
  return (
    <React.Fragment>
      {content.title && (
        <h1 className="documentFirstHeading">{content.title}</h1>
      )}
      {content.description && (
        <p className="documentDescription">{content.description}</p>
      )}
      {content.preview_image && (
        <Image
          className="document-image ui right floated image"
          item={content}
          imageField="preview_image"
          alt=""
        />
      )}
      {content.text && (
        <div
          dangerouslySetInnerHTML={{
            __html: flattenHTMLToAppURL(content.text.data),
          }}
        />
      )}
    </React.Fragment>
  );
};

/**
 * EventView view component class.
 * @function EventView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const EventView = (props) => {
  console.log("[EventView] Custom view loading", props.content);
  const { content, intl } = props;
  const Container =
    config.getComponent({ name: "Container" }).component || SemanticContainer;

  const isOpenEnd = !!content.open_end;
  const allDay = isAllDayEvent(content);
  const start = content.start ? new Date(content.start) : null;
  const end = content.end ? new Date(content.end) : null;

  const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtDate = (d) =>
    `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const fmtTime = (d) =>
    d.toLocaleString('default', { hour: 'numeric', minute: 'numeric', hour12: true });

  // Hide the "Add to calendar" button once the event has passed.
  // Use end date if available, otherwise fall back to start.
  const referenceDate = end || start;
  const isPastEvent = referenceDate ? referenceDate < new Date() : false;

  return (
    <Container id="page-document" className="view-wrapper event-view">
      <div className="event-header-bar dates">
        <div className="event-header-bar__meta">
          {start && (
            <span className="event-header-bar__date">
              <time className="start" dateTime={content.start}>
                {allDay ? fmtDate(start) : `${fmtDate(start)} ${fmtTime(start)}`}
              </time>
              {!isOpenEnd && end && (
                <>
                  {' – '}
                  <time className="end" dateTime={content.end}>
                    {allDay ? fmtDate(end) : `${fmtDate(end)} ${fmtTime(end)}`}
                  </time>
                </>
              )}
            </span>
          )}
          {content?.head_title && (
            <span className="head-title">{content.head_title}</span>
          )}
          {content.location && (
            <span className="event-header-bar__location">
              {content.location}
            </span>
          )}
        </div>
        {!isPastEvent && (
          <a
            className="event-header-bar__ics"
            href={`${flattenToAppURL(content["@id"])}/ics_view`}
            target="_blank"
            rel="noreferrer"
            title={intl.formatMessage(messages.addToCalendar)}
            aria-label={intl.formatMessage(messages.addToCalendar)}
          >
            <Icon name={calendarSVG} aria-hidden="true" />
          </a>
        )}
      </div>
      {hasBlocksData(content) ? (
        <>
          <RenderBlocks {...props} />
        </>
      ) : (
        <EventTextfieldView {...props} />
      )}
    </Container>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
EventView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    text: PropTypes.shape({
      data: PropTypes.string,
    }),
    attendees: PropTypes.arrayOf(PropTypes.string).isRequired,
    contact_email: PropTypes.string,
    contact_name: PropTypes.string,
    contact_phone: PropTypes.string,
    end: PropTypes.string.isRequired,
    event_url: PropTypes.string,
    location: PropTypes.string,
    open_end: PropTypes.bool,
    recurrence: PropTypes.any,
    start: PropTypes.string.isRequired,
    subjects: PropTypes.arrayOf(PropTypes.string).isRequired,
    whole_day: PropTypes.bool,
  }).isRequired,
};

export default injectIntl(EventView);
